import { useState } from "react";
import { Button } from "@/components/ui/button";
import { GoogleFontPicker } from "@/components/settings/GoogleFontPicker";
import { CustomFontFamilyEditor } from "@/components/settings/CustomFontFamilyEditor";
import { useFontFamilies, useCreateFontFamily } from "@/hooks/use-font-families";
import { cssQuote } from "@/components/settings/BrandFontFaceInjector";
import type { BrandFontFamilySlot as Slot } from "@/hooks/use-kit";
import { useTranslation } from "react-i18next";

interface Props {
  label: string;
  brandId: string;
  current: Slot | null;
  disabled?: boolean;
  onChange: (familyId: string | null) => void;
}

export function FontFamilySlot({ label, brandId, current, disabled, onChange }: Props) {
  const { t } = useTranslation("settings");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"google" | "custom">(current?.source === "CUSTOM" ? "custom" : "google");
  const [editingNew, setEditingNew] = useState(false);
  const families = useFontFamilies(brandId);
  const createFamily = useCreateFontFamily(brandId);

  const previewStyle = current?.source === "CUSTOM"
    ? { fontFamily: cssQuote(current.name) }
    : current?.source === "GOOGLE"
      ? { fontFamily: `${cssQuote(current.name)}, sans-serif` }
      : undefined;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">{label}</h3>
          <p className="text-xs text-muted-foreground">{current?.name ?? t("fontFamily.notSet")}</p>
        </div>
        {!disabled && <Button variant="outline" size="sm" onClick={() => setOpen(true)}>{t("fontFamily.change")}</Button>}
      </div>
      <p className="text-2xl truncate" style={previewStyle}>{t("fontFamily.previewText")}</p>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" onClick={() => setOpen(false)}>
          <div className="bg-background rounded-lg p-5 w-[520px] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-2 mb-4">
              <Button variant={tab === "google" ? "default" : "outline"} size="sm" onClick={() => setTab("google")}>{t("fontFamily.googleFonts")}</Button>
              <Button variant={tab === "custom" ? "default" : "outline"} size="sm" onClick={() => setTab("custom")}>{t("fontFamily.custom")}</Button>
            </div>

            {tab === "google" && (
              <GoogleFontFlow
                initial={current?.source === "GOOGLE" ? current.name : ""}
                onSelect={async (name) => {
                  if (!name.trim()) return;
                  const existing = families.data?.find((f) => f.name === name && f.source === "GOOGLE");
                  const fam = existing ?? (await createFamily.mutateAsync({ name: name.trim(), source: "GOOGLE" }));
                  onChange(fam.id);
                  setOpen(false);
                }}
              />
            )}

            {tab === "custom" && (
              editingNew ? (
                <CustomFontFamilyEditor
                  brandId={brandId}
                  onSaved={(id) => { onChange(id); setEditingNew(false); setOpen(false); }}
                  onCancel={() => setEditingNew(false)}
                />
              ) : (
                <div className="space-y-2">
                  <ul className="space-y-1">
                    {(families.data ?? []).filter((f) => f.source === "CUSTOM").map((f) => (
                      <li key={f.id} className="flex items-center justify-between rounded border p-2">
                        <span className="text-sm">{f.name}</span>
                        <Button size="sm" variant="outline" onClick={() => { onChange(f.id); setOpen(false); }}>{t("fontFamily.select")}</Button>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" size="sm" onClick={() => setEditingNew(true)}>{t("fontFamily.newFamily")}</Button>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function GoogleFontFlow({ initial, onSelect }: { initial: string; onSelect: (name: string) => void }) {
  const { t } = useTranslation("settings");
  const [name, setName] = useState(initial);
  return (
    <div className="space-y-3">
      <GoogleFontPicker label={t("fontFamily.familyName")} value={name} onChange={setName} />
      <div className="flex justify-end">
        <Button size="sm" onClick={() => onSelect(name)} disabled={!name.trim()}>{t("fontFamily.select")}</Button>
      </div>
    </div>
  );
}
